"use client";

import { useEffect, useState } from "react";
import Logo from "@/components/Logo";

// Log in (or, the very first time on this Mac, create the password).
export default function LoginPage() {
  const [status, setStatus] = useState<{ hasPassword: boolean; canCreate: boolean } | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/auth").then((r) => r.json()).then(setStatus).catch(() => setError("Couldn't reach the app."));
  }, []);

  const creating = status && !status.hasPassword;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (creating && password !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    const res = await fetch("/api/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: creating ? "create" : "login", password }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) return setError(d.error ?? "Couldn't log in.");
    const next = new URLSearchParams(window.location.search).get("next");
    window.location.href = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <form onSubmit={submit} className="card p-8 w-full max-w-sm space-y-5">
        <div className="flex items-center gap-3">
          <Logo tone="dark" className="h-10 w-auto" />
          <div>
            <div className="font-serif text-xl text-oxblood">Workshop</div>
            <div className="text-xs text-foreground/60">KH Fine Jewelry</div>
          </div>
        </div>

        {!status ? (
          <p className="text-sm text-foreground/50">{error || "Loading..."}</p>
        ) : creating && !status.canCreate ? (
          <p className="text-sm text-foreground/70">
            This app doesn&apos;t have a password yet. Open it on the Mac that runs it (at localhost:3000) to create one.
          </p>
        ) : (
          <>
            {creating && (
              <p className="text-sm text-foreground/70">
                Create a password for the app. You&apos;ll use it to log in from any computer. Use at least 10 characters.
              </p>
            )}
            <div>
              <label className="field-label">{creating ? "New password" : "Password"}</label>
              <input
                type="password"
                className="input-field"
                autoFocus
                autoComplete={creating ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {creating && (
              <div>
                <label className="field-label">Type it again</label>
                <input
                  type="password"
                  className="input-field"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
            )}
            {error && <p className="text-sm text-red-700">{error}</p>}
            <button className="btn-primary w-full" disabled={busy || !password}>
              {busy ? "..." : creating ? "Create password" : "Log in"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
